import PropTypes from "prop-types";
import { Paper, Grid, Button } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { getVendorOrders } from "../../../../api/vendors";
import { useTabs } from "../../../../context/TabContext";
import CustomDataGrid from "../../../../reusable-components/datagrid/CustomDataGrid"
import { COMPLETED_ORDERS } from "../../../../reusable-components/datagrid/orderColumns";
import ManagerChipsWrapper from "../../../../reusable-components/datagrid/ManagerChipsWrapper";
import { DateRangePickerSelect } from "../../../../reusable-components/date-range-picker";
import { isValidDate } from "../../../../utils/validate";
import { HOST_API_URL } from '../../../../config-global';
import Iconify from '../../../../reusable-components/iconify';

VendorOrdersTab.propTypes = {
  selectedRow: PropTypes.object,
};

export default function VendorOrdersTab({ selectedRow }) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { addNewTabAndNavigate } = useTabs();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getVendorOrders(selectedRow.id, searchParams.get('start'), searchParams.get('end'));
      setData(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching vendor orders data:', error);
      setIsLoading(false);
    }
  };

  const setDefaultSearchParams = () => {
    setSearchParams({
      tab: searchParams.get('tab') || '0',
      start: new Date(new Date().setDate(new Date().getDate() - 29)).toISOString(),
      end: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString()
    });
  }

  useEffect(() => {
    setDefaultSearchParams();
  }, []);

  useEffect(() => {
    if(isValidDate(searchParams.get('start')) && isValidDate(searchParams.get('end'))) {
      fetchData();
    }
    else {
      setDefaultSearchParams();
    }
  }, [searchParams]);

  const manager = ManagerChipsWrapper(null, 'Orders');
  const columns = useMemo(() => {
    const arr = [...COMPLETED_ORDERS, manager];
    arr.splice(9, 1);
    return arr;
  }, []);

  const rowClick = (row) => {
    const filter = row.approvedBy ? 'previous' : 'pending';
    navigate(`/dashboard/orders/${row.id}?filter=${filter}&back=vendorOrders&vendor=${selectedRow.id}`);
    };

    const startDate = searchParams.get('start');
    const endDate = searchParams.get('end');
    const downloadVendorOrders = () =>
      window.open(`${HOST_API_URL}Api/Vendor/DownloadVendorOrders?vendorId=${selectedRow.id}&start=${startDate}&end=${endDate}`, '_blank');
        
    const CustomToolbar = () =>
      <Grid
        sx={{ display: 'flex', alignItems: 'center', gap: 1,}}
      >
        <DateRangePickerSelect />
        <Button
          variant="outlined"
          color="secondary"
          sx={{ mt: 1.50 }}
          size="small"
          onClick={() => {
             downloadVendorOrders();
          }}
          startIcon={<Iconify icon="file-icons:microsoft-excel" />}
        >
        Download
        </Button> 
      </Grid>;
  
  return (
      <Paper>         
     <CustomDataGrid
      gridId="vendors-orders"
      applyQuickFilter
      data={data}
      gridColumns={[...columns.slice(0, 7), ...columns.slice(8)]}
      defaultHiddenColumns={['department', 'vendorShipDate', 'itemCount', 'total', 'ticketIds', 'reviews']}
      getRowId={(row) => row.id}
      onRowClick={(params, e) => {
        const selection = window.getSelection().toString();
        if (selection.length > 0) return;
        if (e.deleteChip || e.addChip || e.openTicket || e.combine || e.confirmDialog) return;
        if (e.navigateToTickets) {
          addNewTabAndNavigate(`/dashboard/tickets/${e.navigateToTickets}`);
          return;
        }
        rowClick(params.row);
      }}
      CustomLeftToolbar={CustomToolbar}
      disableRowSelectionOnClick
      checkboxTooltipText="Permission Required"
      checkboxTooltipCheck={(row) => row.locked}
      isRowSelectable={(params) => !params.row.locked}
      isLoading={isLoading}
      advancedFilter
      advancedSearchBase={'pendingOrders'}
              />
    </Paper>
  )
}
